import React, { useCallback, useMemo, useState } from 'react'
import tw, { css } from 'twin.macro'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from 'src/constants/routes'
import { UserPermission, UserProductName } from 'src/constants/user'
import { Button } from 'src/components/shared/Button'
import { SupportModal } from 'src/components/shared/SupportModal'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { getAccessInfo, getIsMasterContractor } from 'src/selectors/user'
import { useSelector } from 'react-redux'
import { GSIcon } from 'src/components/shared/GSIcon'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { getHasCustomersToLink, getHasRequests } from 'src/selectors/mc'

interface ButtonParams {
  id: string
  label?: string
  icon?: any
  selected?: boolean
  showDot?: boolean
  type: 'navigate' | 'separator' | 'action'
  onClick?: () => void
}

export const SideMenu = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const accessInfo = useSelector(getAccessInfo)
  const isMasterContractor = useSelector(getIsMasterContractor)
  const hasRequests = useSelector(getHasRequests)
  const hasCustomersToLink = useSelector(getHasCustomersToLink)

  const { hasPermission, hasSomePermission } = useCheckPermissions()

  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)

  const handleNavigate = useCallback((route: string) => {
    navigate(route)
  }, [])

  const handleCloseSupportModal = useCallback(() => {
    setIsSupportModalOpen(false)
  }, [])

  const sections = useMemo<{ title: string; list: ButtonParams[] }[]>(() => {
    const contractors: ButtonParams[] = [
      {
        id: ROUTES.CONTRACTORS_URL,
        type: 'navigate',
        label: 'Contractors list',
        selected: ROUTES.CONTRACTORS_URL === location.pathname,
        icon: <GSIcon iconName="gs_people" />,
        onClick: () => {
          handleNavigate(ROUTES.CONTRACTORS_URL)
        },
      },
    ]

    if (hasPermission(UserPermission.OnboardingView)) {
      contractors.push({
        id: ROUTES.CONTRACTOR_ONBOARDINGS_URL,
        type: 'navigate',
        label: 'Onboarding',
        selected: ROUTES.CONTRACTOR_ONBOARDINGS_URL === location.pathname,
        icon: <GSIcon iconName="gs_people_add_2" />,
        onClick: () => {
          handleNavigate(ROUTES.CONTRACTOR_ONBOARDINGS_URL)
        },
      })
    }

    const mcs: ButtonParams[] = [
      {
        id: ROUTES.MASTER_CONTRACTORS_URL,
        type: 'navigate',
        label: 'MCs list',
        selected: ROUTES.MASTER_CONTRACTORS_URL === location.pathname,
        icon: <GSIcon iconName="gs_buildings" />,
        onClick: () => {
          handleNavigate(ROUTES.MASTER_CONTRACTORS_URL)
        },
      },
      {
        id: ROUTES.MASTER_CONTRACTOR_ONBOARDINGS_URL,
        type: 'navigate',
        label: 'Onboarding',
        selected: ROUTES.MASTER_CONTRACTOR_ONBOARDINGS_URL === location.pathname,
        icon: <GSIcon iconName="gs_buildings_add" />,
        onClick: () => {
          handleNavigate(ROUTES.MASTER_CONTRACTOR_ONBOARDINGS_URL)
        },
      },
    ]

    const general: ButtonParams[] = []

    if (
      hasPermission(UserPermission.PaymentsView) &&
      accessInfo?.products?.some((item) => item.product === UserProductName.Payments)
    ) {
      general.push({
        id: ROUTES.PAYMENTS_URL,
        type: 'navigate',
        label: 'Payments',
        selected: ROUTES.PAYMENTS_URL === location.pathname,
        icon: <GSIcon iconName="gs_wallet_3" />,
        onClick: () => {
          handleNavigate(ROUTES.PAYMENTS_URL)
        },
      })
    }

    if (isMasterContractor) {
      general.push({
        id: ROUTES.LINK_TO_CUSTOMER_URL,
        type: 'navigate',
        label: 'Link to customer',
        showDot: hasCustomersToLink,
        selected: ROUTES.LINK_TO_CUSTOMER_URL.includes(location.pathname),
        icon: <GSIcon iconName="gs_home_personal_feed" />,
        onClick: () => {
          handleNavigate(ROUTES.LINK_TO_CUSTOMER_URL)
        },
      })
    }

    if (hasSomePermission([UserPermission.UsersView, UserPermission.InvitesView])) {
      general.push({
        id: ROUTES.TEAM_URL,
        type: 'navigate',
        label: 'My Team',
        selected: ROUTES.TEAM_URL.includes(location.pathname),
        icon: <GSIcon iconName="gs_team" />,
        onClick: () => {
          handleNavigate(ROUTES.TEAM_URL)
        },
      })
    }

    if (accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor)) {
      general.push({
        id: ROUTES.REQUESTS_URL,
        type: 'navigate',
        label: 'Requests',
        selected: ROUTES.REQUESTS_URL.includes(location.pathname),
        showDot: hasRequests,
        icon: <GSIcon iconName="gs_paper_plane_top_right" />,
        onClick: () => {
          handleNavigate(ROUTES.REQUESTS_URL)
        },
      })
    }

    general.push({
      id: 'support',
      type: 'action',
      label: 'Support',
      icon: <GSIcon iconName="gs_bubble_annotation_4" />,
      onClick: () => {
        setIsSupportModalOpen(true)
      },
    })

    return [
      {
        title: 'Contractor',
        list: contractors,
      },
      ...(accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor)
        ? [
            {
              title: 'Master contractor',
              list: mcs,
            },
          ]
        : []),
      {
        title: 'General',
        list: general,
      },
    ]
  }, [
    accessInfo,
    hasRequests,
    hasCustomersToLink,
    isMasterContractor,
    location.pathname,
    handleNavigate,
    hasPermission,
    hasSomePermission,
  ])

  return (
    <div
      css={tw`w-[220px] bg-[#EDEDED] flex justify-between flex-col border-0 border-r border-solid border-[#DAD9E0]`}
    >
      <div>
        {sections.map(({ title, list }) => (
          <div key={title} css={tw`p-4 pb-3 border-0 border-b border-solid border-[#DAD9E0]`}>
            <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_500}>
              {title}
            </Typography>
            {list.map(({ id, type, icon, label, selected, showDot, onClick }) => (
              <Button
                key={id}
                leftIcon={icon}
                color={selected ? 'white' : 'grey'}
                className={css(tw`mb-1 w-full justify-start hover:bg-[#F6F6F6] px-2`)}
                size="small"
                iconToEnd
                rightIcon={
                  type === 'action' || showDot ? (
                    <div css={tw`flex items-center justify-center h-5`}>
                      {showDot && <div css={tw`w-2 h-2 rounded-full bg-[#7F00FF] mr-1.5`} />}
                      {type === 'action' && <GSIcon iconName="gs_chevron_right_small" />}
                    </div>
                  ) : undefined
                }
                onClick={onClick}
              >
                {label}
              </Button>
            ))}
          </div>
        ))}
      </div>
      <SupportModal isOpen={isSupportModalOpen} onClose={handleCloseSupportModal} />
    </div>
  )
}
